/* Based · Cart drawer (vanilla JS controller).
 * Replaces Horizon's stock cart-drawer-component for the based theme.
 *
 * Responsibilities:
 *   - open/close the <dialog class="cart-drawer__dialog based-cart"> drawer
 *   - listen for `cart:update`, refetch /cart.js, re-render line items + reward bar + totals
 *   - bind quantity stepper, trash button to /cart/change.js then re-dispatch cart:update
 *   - update header cart icon bubble via the canonical CartUpdateEvent shape
 */

(function () {
  'use strict';

  var DRAWER_SELECTOR = 'dialog.cart-drawer__dialog.based-cart';
  var TRIGGER_SELECTOR = '[data-based-cart-trigger]';
  var ROUTINE_SELECTOR = '[data-routine-convertflow]';

  function normalizedText(el) {
    return ((el && el.textContent) || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function isCartPath(href) {
    if (!href) return false;
    try {
      var url = new URL(href, window.location.origin);
      var allowedHost = url.hostname === window.location.hostname ||
        url.hostname === 'based.com' ||
        url.hostname === 'basedbodyworks.myshopify.com' ||
        /\.shopifypreview\.com$/.test(url.hostname);
      if (!allowedHost) return false;
      return url.pathname.replace(/\/+$/, '') === '/cart';
    } catch (e) {
      return false;
    }
  }

  // ---------- formatting ----------
  // Mirrors Shopify's money formatter for the most common formats. Shop money_format
  // is passed via the JSON config block. We support: amount_with_comma_separator,
  // amount, amount_no_decimals, amount_with_apostrophe_separator. Falls back to USD.
  function formatMoney(cents, format) {
    if (cents == null || isNaN(cents)) cents = 0;
    var fmt = format || '${{amount}}';
    var value;
    function dec(n, d, t, decimals) {
      if (decimals == null) decimals = 2;
      var num = (n / 100).toFixed(decimals);
      var parts = num.split('.');
      var int = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
      return decimals > 0 ? int + d + parts[1] : int;
    }
    var placeholder = fmt.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return fmt;
    switch (placeholder[1]) {
      case 'amount':
        value = dec(cents, '.', ',', 2);
        break;
      case 'amount_no_decimals':
        value = dec(cents, '.', ',', 0);
        break;
      case 'amount_with_comma_separator':
        value = dec(cents, ',', '.', 2);
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = dec(cents, ',', '.', 0);
        break;
      case 'amount_with_apostrophe_separator':
        value = dec(cents, '.', "'", 2);
        break;
      case 'amount_no_decimals_with_space_separator':
        value = dec(cents, '.', ' ', 0);
        break;
      case 'amount_with_space_separator':
        value = dec(cents, '.', ' ', 2);
        break;
      default:
        value = dec(cents, '.', ',', 2);
    }
    return fmt.replace(placeholder[0], value);
  }

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(s) {
    return escapeHtml(s);
  }

  function cartItemHandle(item) {
    if (!item) return '';
    if (item.handle) return String(item.handle);
    var url = item.url ? String(item.url) : '';
    var match = url.match(/\/products\/([^/?#]+)/);
    return match && match[1] ? decodeURIComponent(match[1]) : '';
  }

  // ---------- icons (re-used in client-side render) ----------
  var ICON_TRASH =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256"><path d="M216,48H180V36A28,28,0,0,0,152,8H104A28,28,0,0,0,76,36V48H40a12,12,0,0,0,0,24h4V208a20,20,0,0,0,20,20H192a20,20,0,0,0,20-20V72h4a12,12,0,0,0,0-24ZM100,36a4,4,0,0,1,4-4h48a4,4,0,0,1,4,4V48H100Zm88,168H68V72H188ZM116,104v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Zm48,0v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Z"/></svg>';
  var ICON_MINUS =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256"><path d="M228,128a12,12,0,0,1-12,12H40a12,12,0,0,1,0-24H216A12,12,0,0,1,228,128Z"/></svg>';
  var ICON_PLUS =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256"><path d="M228,128a12,12,0,0,1-12,12H140v76a12,12,0,0,1-24,0V140H40a12,12,0,0,1,0-24h76V40a12,12,0,0,1,24,0v76h76A12,12,0,0,1,228,128Z"/></svg>';
  var ICON_TAG =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 256 256"><path d="M243.31,136,144,36.69A15.86,15.86,0,0,0,132.69,32H40a8,8,0,0,0-8,8v92.69A15.86,15.86,0,0,0,36.69,144L136,243.31a16,16,0,0,0,22.63,0l84.68-84.68a16,16,0,0,0,0-22.63ZM84,96A12,12,0,1,1,96,84,12,12,0,0,1,84,96Z"/></svg>';

  // ---------- controller ----------
  function BasedCart(dialog) {
    this.dialog = dialog;
    this.config = this.readConfig();
    this.productHydration = {};
    this.imageWidth = 200;
    this.refreshTimer = null;
    this.refreshPromise = null;
    this.refreshQueued = false;
    this.bind();
    this.openFromUrl();
  }

  BasedCart.prototype.readConfig = function () {
    var node = this.dialog.querySelector('script[data-based-cart-config]');
    var fallback = { tier_threshold_cents: 0, tier_label: '', currency: 'USD', money_format: '${{amount}}', variant_compare_at: {}, variant_cart_titles: {}, sets_variant_ids: [], upsells: [] };
    if (!node) return fallback;
    try {
      var c = JSON.parse(node.textContent || '{}');
      c.variant_compare_at = c.variant_compare_at || {};
      c.variant_cart_titles = c.variant_cart_titles || {};
      c.sets_variant_ids = c.sets_variant_ids || [];
      c.upsells = c.upsells || [];
      return c;
    } catch (e) {
      return fallback;
    }
  };

  BasedCart.prototype.compareAtFor = function (item) {
    var byVariant = this.config.variant_compare_at && this.config.variant_compare_at[item.variant_id];
    if (byVariant && byVariant !== 0) return byVariant;
    if (item.compare_at_price && item.compare_at_price !== 0) return item.compare_at_price;
    return null;
  };

  BasedCart.prototype.visibleItems = function (cart) {
    return ((cart && cart.items) || []).filter(function (item) {
      return !(item.properties && item.properties._instant_hidden === 'true' || item.properties && item.properties['_instant-hidden'] === 'true');
    });
  };

  BasedCart.prototype.hydrateCompareAt = function (items) {
    var self = this;
    var handles = {};

    (items || []).forEach(function (item) {
      if (!item || self.compareAtFor(item)) return;
      var handle = cartItemHandle(item);
      if (handle) handles[handle] = true;
    });

    var handleList = Object.keys(handles);
    if (!handleList.length) return Promise.resolve();

    return Promise.all(handleList.map(function (handle) {
      if (!self.productHydration[handle]) {
        self.productHydration[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js', {
          headers: { 'Accept': 'application/json' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('product json failed: ' + r.status);
            return r.json();
          })
          .then(function (product) {
            (product.variants || []).forEach(function (variant) {
              var compareAt = Number(variant.compare_at_price || 0);
              var price = Number(variant.price || 0);
              if (compareAt > price) {
                self.config.variant_compare_at[variant.id] = compareAt;
              }
            });
          })
          .catch(function (err) {
            console.warn('[based-cart] compare-at hydration failed for ' + handle, err);
          });
      }
      return self.productHydration[handle];
    })).then(function () {});
  };

  BasedCart.prototype.renderWithHydration = function (cart) {
    var self = this;
    return this.hydrateCompareAt(this.visibleItems(cart)).then(function () {
      self.render(cart);
      return cart;
    });
  };

  BasedCart.prototype.money = function (cents) {
    return formatMoney(cents, this.config.money_format);
  };

  function isDefaultVariantTitle(title) {
    var value = String(title || '').trim().toLowerCase();
    return !value ||
      value === 'default title' ||
      value === 'default';
  }

  function isGenericVariantTitle(title) {
    var value = String(title || '').trim().toLowerCase();
    return isDefaultVariantTitle(value) ||
      value === 'single' ||
      value === 'just one' ||
      value === 'just shampoo' ||
      value === 'just conditioner' ||
      value === 'one pack' ||
      value === '1 pack' ||
      value === 'two pack' ||
      value === 'pack of 2' ||
      value === 'three pack' ||
      value === 'pack of 3';
  }

  function isBundleVariantTitle(title) {
    var value = String(title || '').trim().toLowerCase();
    return /\b(duo|trio|kit|set|bundle|essentials)\b/.test(value);
  }

  BasedCart.prototype.itemDisplayTitles = function (item, variantTitle) {
    var productTitle = item.product_title || item.title || '';
    var configuredTitle = this.config.variant_cart_titles && this.config.variant_cart_titles[item.variant_id];
    var displayTitle = productTitle;
    var displaySubtitle = '';

    if (configuredTitle && !isGenericVariantTitle(configuredTitle)) {
      displayTitle = configuredTitle;
      if (variantTitle && variantTitle !== configuredTitle && !isDefaultVariantTitle(variantTitle)) {
        displaySubtitle = variantTitle;
      }
    } else if (variantTitle && !isDefaultVariantTitle(variantTitle) && isBundleVariantTitle(variantTitle)) {
      displayTitle = variantTitle;
    } else if (variantTitle && !isDefaultVariantTitle(variantTitle)) {
      displaySubtitle = variantTitle;
    }

    return { title: displayTitle, subtitle: displaySubtitle };
  };

  BasedCart.prototype.bind = function () {
    var self = this;

    // Keep routine/cart links in the drawer experience, including ConvertFlow
    // buttons that switch from "Add To Cart" to "View Cart" after an item adds.
    document.addEventListener('click', function (e) {
      var cartLink = e.target.closest && e.target.closest('a[href]');
      if (cartLink && isCartPath(cartLink.getAttribute('href'))) {
        e.preventDefault();
        e.stopImmediatePropagation();
        self.refreshAndOpen();
        return;
      }

      var productButton = e.target.closest && e.target.closest(ROUTINE_SELECTOR + ' .cf-products-item-button');
      var cfButton = e.target.closest && e.target.closest(ROUTINE_SELECTOR + ' .cf-button');
      var routineAction = productButton || cfButton;
      if (routineAction) {
        var actionText = normalizedText(routineAction);
        if (actionText.indexOf('view cart') !== -1) {
          e.preventDefault();
          e.stopImmediatePropagation();
          self.refreshAndOpen();
          return;
        }

        if (productButton || actionText.indexOf('add to cart') !== -1 || actionText.indexOf('get the full routine') !== -1) {
          self.openAfterExternalCartAction();
        }
      }
    }, true);

    // Open via header trigger or any `[data-based-cart-trigger]`
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest && e.target.closest(TRIGGER_SELECTOR);
      if (trigger) {
        e.preventDefault();
        self.refreshAndOpen();
        return;
      }
    });

    // Close button
    this.dialog.addEventListener('click', function (e) {
      if (e.target.closest('[data-based-cart-close]')) {
        e.preventDefault();
        self.close();
        return;
      }
      // Click on dialog backdrop (the dialog element itself, outside the panel) closes
      if (e.target === self.dialog) {
        self.close();
      }
    });

    // ESC key handled natively by <dialog>; listen for cancel to stay in sync
    this.dialog.addEventListener('cancel', function () {
      // default close, no-op
    });

    // Quantity steppers + trash
    this.dialog.addEventListener('click', function (e) {
      var dec = e.target.closest('[data-based-qty-decrement]');
      var inc = e.target.closest('[data-based-qty-increment]');
      var rm = e.target.closest('[data-based-cart-remove]');
      var upsell = e.target.closest('[data-based-upsell-add]');
      if (dec) {
        e.preventDefault();
        self.changeQty(dec, -1);
      } else if (inc) {
        e.preventDefault();
        self.changeQty(inc, +1);
      } else if (rm) {
        e.preventDefault();
        self.removeItem(rm);
      } else if (upsell) {
        e.preventDefault();
        self.addUpsell(upsell);
      }
    });

    // Manual qty input (debounced)
    var qtyDebounce;
    this.dialog.addEventListener('change', function (e) {
      var input = e.target.closest('[data-based-qty-input], [data-based-qty-select]');
      if (!input) return;
      clearTimeout(qtyDebounce);
      qtyDebounce = setTimeout(function () {
        self.setQty(input, parseInt(input.value, 10) || 0);
      }, 100);
    });

    // External cart:update events from PDP ATC, best-sellers etc.
    document.addEventListener('cart:update', function (e) {
      var sourceId = e && e.detail && e.detail.sourceId;
      if (sourceId === 'based-cart-drawer') return; // ignore our own
      self.handleExternalCartUpdate(e);
    });

    document.addEventListener('based:cart:open', function () {
      self.refreshAndOpen();
    });

    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('.based-product-recommendations .based-card, .based-related .based-card');
      if (!form || !form.querySelector('[data-quick-add]')) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      self.addQuickAddForm(form);
    }, true);
  };

  BasedCart.prototype.open = function () {
    if (!this.dialog.open && typeof this.dialog.showModal === 'function') {
      try {
        this.dialog.showModal();
      } catch (e) {
        // Older browsers without showModal support
        this.dialog.setAttribute('open', '');
      }
    }
  };

  BasedCart.prototype.refreshAndOpen = function () {
    this.open();
    return this.refresh();
  };

  BasedCart.prototype.openAfterExternalCartAction = function () {
    var self = this;
    self.open();
    self.queueRefresh();
    [650, 1400, 2600].forEach(function (delay) {
      window.setTimeout(function () {
        self.refreshAndOpen();
      }, delay);
    });
  };

  BasedCart.prototype.openFromUrl = function () {
    var shouldOpen = false;
    try {
      shouldOpen = new URLSearchParams(window.location.search).has('open-cart');
    } catch (e) {
      shouldOpen = window.location.search.indexOf('open-cart') !== -1;
    }
    if (!shouldOpen) return;

    var self = this;
    window.setTimeout(function () {
      self.refreshAndOpen();
    }, 100);
  };

  BasedCart.prototype.close = function () {
    if (this.dialog.open && typeof this.dialog.close === 'function') {
      this.dialog.close();
    } else {
      this.dialog.removeAttribute('open');
    }
  };

  // ---------- mutations ----------
  BasedCart.prototype.changeQty = function (btn, delta) {
    var item = btn.closest('[data-based-cart-item]');
    if (!item) return;
    var input = item.querySelector('[data-based-qty-input], [data-based-qty-select]');
    var current = input ? (parseInt(input.value, 10) || 0) : 1;
    this.setQty(btn, current + delta);
  };

  BasedCart.prototype.setQty = function (el, qty) {
    var item = el.closest('[data-based-cart-item]');
    if (!item) return;
    var key = item.dataset.lineKey;
    if (!key) return;
    if (qty < 0) qty = 0;
    this.cartChange(key, qty, item);
  };

  BasedCart.prototype.removeItem = function (btn) {
    var item = btn.closest('[data-based-cart-item]');
    if (!item) return;
    var key = item.dataset.lineKey;
    if (!key) return;
    this.cartChange(key, 0, item);
  };

  BasedCart.prototype.addUpsell = function (btn) {
    var item = btn.closest('[data-based-upsell]');
    if (!item || !item.dataset.variantId) return;
    var self = this;
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'ADDING...';
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: item.dataset.variantId, quantity: 1 })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart add failed: ' + r.status);
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return self.renderWithHydration(cart).then(function () {
          self.dispatchUpdate(cart);
        });
      })
      .catch(function (err) {
        console.error('[based-cart] upsell add failed', err);
        btn.disabled = false;
      })
      .finally(function () {
        btn.disabled = false;
        btn.textContent = label || 'ADD';
      });
  };

  BasedCart.prototype.addQuickAddForm = function (form) {
    if (!form || form.dataset.basedQuickAddLoading === 'true') return;
    var self = this;
    var btn = form.querySelector('[data-quick-add]');
    var label = btn && btn.textContent;
    form.dataset.basedQuickAddLoading = 'true';
    if (btn) {
      btn.disabled = true;
      btn.textContent = 'ADDING...';
    }

    var action = form.action || '/cart/add.js';
    action = action.replace(/\/cart\/add(?:\?.*)?$/, '/cart/add.js');
    return fetch(action, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      body: new FormData(form)
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart add failed: ' + r.status);
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return self.renderWithHydration(cart).then(function () {
          self.dispatchUpdate(cart);
          self.open();
        });
      })
      .catch(function (err) {
        console.error('[based-cart] quick add failed', err);
        form.submit();
      })
      .finally(function () {
        form.dataset.basedQuickAddLoading = 'false';
        if (btn) {
          btn.disabled = false;
          btn.textContent = label || 'ADD TO CART';
        }
      });
  };

  BasedCart.prototype.cartChange = function (key, qty, item) {
    var self = this;
    var isRemoval = qty === 0;
    self.setItemPending(item, isRemoval ? 'removing' : 'updating');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart change failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        return self.renderWithHydration(cart).then(function () {
          self.dispatchUpdate(cart);
        });
      })
      .catch(function (err) {
        console.error('[based-cart] change failed', err);
        self.clearItemPending(item);
      })
      .finally(function () {
        if (!isRemoval) self.clearItemPending(item);
      });
  };

  BasedCart.prototype.handleExternalCartUpdate = function (event) {
    var cart = event && event.detail && event.detail.resource;
    if (cart && Array.isArray(cart.items)) {
      this.renderWithHydration(cart);
      return;
    }
    this.queueRefresh();
  };

  BasedCart.prototype.queueRefresh = function () {
    var self = this;
    if (self.refreshPromise) {
      self.refreshQueued = true;
      return self.refreshPromise;
    }

    if (self.refreshTimer) clearTimeout(self.refreshTimer);
    self.refreshTimer = setTimeout(function () {
      self.refreshTimer = null;
      self.refresh();
    }, 90);
    return null;
  };

  BasedCart.prototype.refresh = function () {
    var self = this;
    if (self.refreshPromise) {
      self.refreshQueued = true;
      return self.refreshPromise;
    }

    self.dialog.setAttribute('data-refreshing', 'true');
    self.refreshPromise = fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return self.renderWithHydration(cart); })
      .catch(function (err) { console.error('[based-cart] refresh failed', err); })
      .finally(function () {
        self.dialog.removeAttribute('data-refreshing');
        self.refreshPromise = null;
        if (self.refreshQueued) {
          self.refreshQueued = false;
          self.queueRefresh();
        }
      });
    return self.refreshPromise;
  };

  BasedCart.prototype.setItemPending = function (item, state) {
    if (!item) return;
    var pendingState = state || 'updating';
    var controls = item.querySelectorAll('button, input, select');
    controls.forEach(function (control) { control.disabled = true; });
    item.setAttribute('aria-busy', 'true');
    if (pendingState === 'removing') {
      item.style.maxHeight = item.offsetHeight + 'px';
      item.getBoundingClientRect(); // force layout before collapse transition
      window.requestAnimationFrame(function () {
        item.dataset.cartPending = 'removing';
        item.style.maxHeight = '0px';
      });
    } else {
      item.dataset.cartPending = pendingState;
    }
  };

  BasedCart.prototype.clearItemPending = function (item) {
    if (!item || !item.isConnected) return;
    var controls = item.querySelectorAll('button, input, select');
    controls.forEach(function (control) { control.disabled = false; });
    delete item.dataset.cartPending;
    item.removeAttribute('aria-busy');
    item.style.maxHeight = '';
  };

  BasedCart.prototype.dispatchUpdate = function (cart) {
    var visibleCount = (cart.items || []).reduce(function (count, item) {
      if (item.properties && (item.properties._instant_hidden === 'true' || item.properties['_instant-hidden'] === 'true')) {
        return count;
      }
      return count + (item.quantity || 0);
    }, 0);
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'based-cart-drawer',
        data: {
          source: 'cart-drawer',
          itemCount: visibleCount,
          sections: {}
        }
      }
    }));
  };

  // ---------- render ----------
  BasedCart.prototype.render = function (cart) {
    var visible = this.visibleItems(cart);
    var totalPrice = cart.total_price || 0;
    var visibleOriginalSubtotal = visible.reduce(function (n, i) { return n + (i.original_line_price || i.line_price || 0); }, 0);
    var visibleCount = visible.reduce(function (n, i) { return n + (i.quantity || 0); }, 0);

    // Toggle empty/body
    var body = this.dialog.querySelector('[data-based-cart-body]');
    var empty = this.dialog.querySelector('[data-based-cart-empty]');
    var countWrap = this.dialog.querySelector('[data-based-cart-count-wrap]');
    var countNum = this.dialog.querySelector('[data-based-cart-count]');
    if (visibleCount > 0) {
      if (body) body.removeAttribute('hidden');
      if (empty) empty.setAttribute('hidden', '');
      if (countWrap) countWrap.removeAttribute('hidden');
    } else {
      if (body) body.setAttribute('hidden', '');
      if (empty) empty.removeAttribute('hidden');
      if (countWrap) countWrap.setAttribute('hidden', '');
    }
    if (countNum) countNum.textContent = String(visibleCount);

    // Reward bar
    this.renderReward(visibleOriginalSubtotal);

    // Items
    this.renderItems(visible);
    this.renderUpsells(visible);

    // Discounts
    this.renderDiscounts(cart.cart_level_discount_applications || []);

    // Total savings
    this.renderSavings(visible);

    // Checkout total
    var totalEl = this.dialog.querySelector('[data-based-cart-total]');
    if (totalEl) totalEl.textContent = this.money(totalPrice);
  };

  BasedCart.prototype.renderReward = function (totalPrice) {
    var threshold = this.config.tier_threshold_cents || 0;
    var label = this.config.tier_label || '';
    var pct = threshold > 0 ? Math.min(100, Math.floor((totalPrice / threshold) * 100)) : 0;

    var reward = this.dialog.querySelector('[data-based-reward]');
    if (!reward) return;
    reward.dataset.rewardState = pct === 100 ? 'active' : 'inactive';

    var fill = this.dialog.querySelector('[data-based-reward-fill]');
    if (fill) fill.style.setProperty('--based-reward-progress', pct + '%');

    var prog = this.dialog.querySelector('[data-based-reward-progress]');
    var unlocked = this.dialog.querySelector('[data-based-reward-unlocked]');
    if (pct === 100) {
      if (prog) prog.setAttribute('hidden', '');
      if (unlocked) unlocked.removeAttribute('hidden');
    } else {
      if (prog) prog.removeAttribute('hidden');
      if (unlocked) unlocked.setAttribute('hidden', '');
    }

    var amount = this.dialog.querySelector('[data-based-reward-amount]');
    if (amount) amount.textContent = this.money(Math.max(0, threshold - totalPrice));

    var tier = this.dialog.querySelector('[data-based-reward-tier]');
    if (tier) tier.textContent = label;

    var unlockedText = unlocked ? unlocked.querySelector('.based-cart__reward-unlocked-text') : null;
    if (unlockedText) unlockedText.textContent = label + ' UNLOCKED';

    var tierIconWrap = this.dialog.querySelector('.based-cart__reward-tier');
    if (tierIconWrap) tierIconWrap.dataset.rewardTierState = pct === 100 ? 'active' : '';
  };

  BasedCart.prototype.renderItems = function (items) {
    var self = this;
    var container = this.dialog.querySelector('[data-based-cart-items]');
    if (!container) return;
    var html = items.map(function (item) {
      var compareAtLine = 0;
      var unitCompare = self.compareAtFor(item);
      var finalLine = item.final_line_price != null ? item.final_line_price : (item.line_price || 0);
      if (unitCompare && unitCompare !== 0) {
        compareAtLine = unitCompare * item.quantity;
      } else if (item.original_line_price > finalLine) {
        compareAtLine = item.original_line_price;
      } else {
        compareAtLine = 0;
      }
      var saveAmount = (compareAtLine > 0 && compareAtLine > finalLine) ? compareAtLine - finalLine : 0;
      var img = item.image || item.featured_image && item.featured_image.url || '';
      var imgUrl = img ? (img.indexOf('?') >= 0 ? img : img + (img.indexOf('//') > 0 ? '?width=200' : '')) : '';

      // Variant title: hide only default-only variants. Pack/quantity titles
      // like "Just one" are meaningful cart descriptors.
      var variantTitle = item.variant_title || '';
      if (variantTitle === 'Default Title' || variantTitle === 'Default') variantTitle = '';
      if (item.product_has_only_default_variant) variantTitle = '';
      var displayTitles = self.itemDisplayTitles(item, variantTitle);

      var freeGiftBlock = finalLine === 0
        ? '<span class="based-cart__item-free-gift">FREE GIFT</span>'
        : '';

      var compareBlock = (compareAtLine > 0 && compareAtLine > finalLine)
        ? '<span class="based-cart__item-compare">' + self.money(compareAtLine) + '</span>'
        : '';

      var saveBlock = saveAmount > 0
        ? '<p class="based-cart__item-save">(Save ' + self.money(saveAmount) + ')</p>'
        : '';

      return ''
        + '<form class="based-cart__item" data-based-cart-item data-line-key="' + escapeAttr(item.key) + '" data-product-id="' + escapeAttr(item.product_id) + '" data-variant-id="' + escapeAttr(item.variant_id) + '">'
        +   '<div class="based-cart__item-image">'
        +     '<img alt="' + escapeAttr(item.product_title || '') + '" src="' + escapeAttr(imgUrl) + '" width="80" height="80" loading="eager" decoding="async">'
        +   '</div>'
        +   '<div class="based-cart__item-content">'
        +     '<div class="based-cart__item-top">'
        +       '<div class="based-cart__item-info">'
        +         '<div class="based-cart__item-titles">'
        +           '<div class="based-cart__item-title-row">'
        +             '<a class="based-cart__item-title" href="' + escapeAttr(item.url || '') + '" rel="noopener">' + escapeHtml(displayTitles.title) + '</a>'
        +             freeGiftBlock
        +           '</div>'
        +           '<p class="based-cart__item-variant">' + escapeHtml(displayTitles.subtitle) + '</p>'
        +         '</div>'
        +         '<button type="button" class="based-cart__item-remove" data-based-cart-remove aria-label="Remove item">'
        +           '<span class="based-cart__item-remove-icon">' + ICON_TRASH + '</span>'
        +         '</button>'
        +       '</div>'
        +     '</div>'
        +     '<div class="based-cart__item-bottom">'
        +       '<div class="based-cart__qty">'
        +         renderQtySelect(item.quantity)
        +       '</div>'
        +       '<div class="based-cart__item-price-col">'
        +         '<div class="based-cart__item-prices">'
        +           '<div class="based-cart__item-prices-inner">'
        +             compareBlock
        +             '<p class="based-cart__item-final">' + self.money(finalLine) + '</p>'
        +           '</div>'
        +         '</div>'
        +         saveBlock
        +       '</div>'
        +     '</div>'
        +   '</div>'
        + '</form>';
    }).join('');
    container.innerHTML = html;
  };

  function renderQtySelect(quantity) {
    var current = Number(quantity) || 1;
    var html = '<select class="based-cart__qty-select" data-based-qty-select aria-label="Quantity">';
    for (var i = 1; i <= 5; i++) {
      html += '<option value="' + i + '"' + (current === i ? ' selected' : '') + '>' + i + '</option>';
    }
    if (current > 5) {
      html += '<option value="' + current + '" selected>' + current + '</option>';
    }
    html += '</select>';
    return html;
  }

  BasedCart.prototype.renderUpsells = function (items) {
    var wrap = this.dialog.querySelector('[data-based-upsells]');
    var list = wrap && wrap.querySelector('.based-cart__upsells-list');
    if (!wrap || !list) return;

    var inCart = {};
    items.forEach(function (item) { inCart[item.product_id] = true; });
    var shown = (this.config.upsells || []).filter(function (upsell) {
      return !inCart[upsell.product_id];
    }).slice(0, 12);

    if (!shown.length) {
      wrap.setAttribute('hidden', '');
      list.innerHTML = '';
      return;
    }

    wrap.removeAttribute('hidden');
    list.innerHTML = shown.map(function (upsell) {
      var compare = upsell.compare_at_price && upsell.compare_at_price > upsell.price
        ? '<span class="based-cart__upsell-compare">' + formatMoney(upsell.compare_at_price, this.config.money_format) + '</span>'
        : '';
      var img = upsell.image
        ? '<img src="' + escapeAttr(upsell.image) + '" alt="' + escapeAttr(upsell.title || '') + '" width="60" height="60" loading="lazy" decoding="async">'
        : '';
      return ''
        + '<div class="based-cart__upsell" data-based-upsell data-product-id="' + escapeAttr(upsell.product_id) + '" data-variant-id="' + escapeAttr(upsell.variant_id) + '">'
        +   '<a class="based-cart__upsell-image" href="' + escapeAttr(upsell.url || '') + '" rel="noopener">' + img + '</a>'
        +   '<a class="based-cart__upsell-title" href="' + escapeAttr(upsell.url || '') + '" rel="noopener">' + escapeHtml(upsell.title || '') + '</a>'
        +   '<div class="based-cart__upsell-price">' + compare + '<span class="based-cart__upsell-final">' + formatMoney(upsell.price || 0, this.config.money_format) + '</span></div>'
        +   '<button class="based-cart__upsell-add" type="button" data-based-upsell-add>ADD</button>'
        + '</div>';
    }, this).join('');
  };

  BasedCart.prototype.renderDiscounts = function (discounts) {
    var self = this;
    var container = this.dialog.querySelector('[data-based-discounts]');
    if (!container) return;
    if (!discounts || !discounts.length) {
      container.setAttribute('hidden', '');
      container.innerHTML = '';
      return;
    }
    container.removeAttribute('hidden');
    container.innerHTML = discounts.map(function (d) {
      return ''
        + '<div class="based-cart__discount">'
        +   '<span class="based-cart__discount-icon">' + ICON_TAG + '</span>'
        +   '<p class="based-cart__discount-name">' + escapeHtml(d.title || '') + '</p>'
        +   '<div class="based-cart__discount-amount-row">'
        +     '<span class="based-cart__discount-paren">(</span>'
        +     '<p class="based-cart__discount-amount">-' + self.money(d.total_allocated_amount || 0) + '</p>'
        +     '<span class="based-cart__discount-paren">)</span>'
        +   '</div>'
        + '</div>';
    }).join('');
  };

  BasedCart.prototype.renderSavings = function (items) {
    var self = this;
    var total = 0;
    items.forEach(function (item) {
      var unitCompare = self.compareAtFor(item);
      var compareAtLine = 0;
      var finalLine = item.final_line_price != null ? item.final_line_price : (item.line_price || 0);
      if (unitCompare && unitCompare !== 0) {
        compareAtLine = unitCompare * item.quantity;
      } else if (item.original_line_price > finalLine) {
        compareAtLine = item.original_line_price;
      }
      if (compareAtLine > finalLine) total += (compareAtLine - finalLine);
    });
    var row = this.dialog.querySelector('[data-based-savings-row]');
    var amount = this.dialog.querySelector('[data-based-savings-amount]');
    if (total <= 0) {
      if (row) row.setAttribute('hidden', '');
    } else {
      if (row) row.removeAttribute('hidden');
      if (amount) amount.textContent = this.money(total);
    }
  };

  // ---------- init ----------
  function init() {
    var dialog = document.querySelector(DRAWER_SELECTOR);
    if (!dialog) return;
    if (dialog.__basedCart) return;
    dialog.__basedCart = new BasedCart(dialog);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
